// Drag snapping and multi-select alignment, all in fitz page space.
//
// Snapping compares the moved selection's left/centre/right (and top/
// centre/bottom) against candidate lines — page edges + centre and the
// edges/centres of the other annotations on the page — and returns the
// small correction that locks the nearest pair, plus the matched line
// coordinates so the UI can draw smart guides.
import { annotBounds, translateAnnot } from "./annot-geometry.ts";
import { isMarkup, type Annotation } from "./protocol.ts";

export type Box = [number, number, number, number];

export interface SnapTargets {
  /** Candidate vertical lines (x coordinates). */
  x: number[];
  /** Candidate horizontal lines (y coordinates). */
  y: number[];
}

export interface SnapResult {
  dx: number;
  dy: number;
  /** Matched vertical guide (x), if the x axis snapped. */
  vGuide: number | null;
  /** Matched horizontal guide (y), if the y axis snapped. */
  hGuide: number | null;
}

export const NO_SNAP: SnapResult = { dx: 0, dy: 0, vGuide: null, hGuide: null };

/** Candidate lines for a drag on `pageIndex`: page edges + centre, and
 *  every other annotation's edges + centre. */
export function buildSnapTargets(
  annots: readonly Annotation[],
  pageIndex: number,
  excludeIds: ReadonlySet<string>,
  pageWidth: number,
  pageHeight: number
): SnapTargets {
  const x: number[] = [0, pageWidth / 2, pageWidth];
  const y: number[] = [0, pageHeight / 2, pageHeight];
  for (const a of annots) {
    if (a.pageIndex !== pageIndex || excludeIds.has(a.id)) continue;
    const b = annotBounds(a);
    x.push(b[0], (b[0] + b[2]) / 2, b[2]);
    y.push(b[1], (b[1] + b[3]) / 2, b[3]);
  }
  return { x, y };
}

/** The correction that snaps `bounds` (already moved by the raw drag
 *  delta) to the nearest candidate line within `threshold`, per axis. */
export function computeSnap(bounds: Box, targets: SnapTargets, threshold: number): SnapResult {
  const edgesX = [bounds[0], (bounds[0] + bounds[2]) / 2, bounds[2]];
  const edgesY = [bounds[1], (bounds[1] + bounds[3]) / 2, bounds[3]];
  let dx = 0;
  let dy = 0;
  let vGuide: number | null = null;
  let hGuide: number | null = null;
  let bestX = threshold;
  for (const t of targets.x) {
    for (const e of edgesX) {
      const d = Math.abs(t - e);
      if (d < bestX) {
        bestX = d;
        dx = t - e;
        vGuide = t;
      }
    }
  }
  let bestY = threshold;
  for (const t of targets.y) {
    for (const e of edgesY) {
      const d = Math.abs(t - e);
      if (d < bestY) {
        bestY = d;
        dy = t - e;
        hGuide = t;
      }
    }
  }
  return { dx, dy, vGuide, hGuide };
}

// ───────────────────────── align / distribute ─────────────────────────

export type AlignMode = "left" | "centerH" | "right" | "top" | "middle" | "bottom";

/** Markup is glued to the text it marks — it never moves with align/
 *  distribute (same rule as drag-to-move). */
function movable(a: Annotation): boolean {
  return !isMarkup(a);
}

/** Align a set of boxes against their common bounding box. Returns the
 *  displacement per id — kind-agnostic, so one implementation serves
 *  annotations, page content and form fields alike. */
export function alignBoxes(
  items: readonly { id: string; bbox: Box }[],
  mode: AlignMode
): Map<string, [number, number]> {
  const deltas = new Map<string, [number, number]>();
  if (items.length < 2) return deltas;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const { bbox: b } of items) {
    x0 = Math.min(x0, b[0]);
    y0 = Math.min(y0, b[1]);
    x1 = Math.max(x1, b[2]);
    y1 = Math.max(y1, b[3]);
  }
  for (const { id, bbox: b } of items) {
    let dx = 0;
    let dy = 0;
    switch (mode) {
      case "left": dx = x0 - b[0]; break;
      case "centerH": dx = (x0 + x1) / 2 - (b[0] + b[2]) / 2; break;
      case "right": dx = x1 - b[2]; break;
      case "top": dy = y0 - b[1]; break;
      case "middle": dy = (y0 + y1) / 2 - (b[1] + b[3]) / 2; break;
      case "bottom": dy = y1 - b[3]; break;
    }
    deltas.set(id, [dx, dy]);
  }
  return deltas;
}

/** Equal gaps between boxes along one axis; the outermost two stay put.
 *  Needs ≥3 boxes. Displacement per id, like alignBoxes. */
export function distributeBoxes(
  items: readonly { id: string; bbox: Box }[],
  axis: "h" | "v"
): Map<string, [number, number]> {
  const deltas = new Map<string, [number, number]>();
  const lo = axis === "h" ? 0 : 1;
  const hi = axis === "h" ? 2 : 3;
  const sorted = [...items].sort((p, q) => p.bbox[lo] - q.bbox[lo]);
  if (sorted.length < 3) return deltas;
  const first = sorted[0]!;
  const last = sorted[sorted.length - 1]!;
  const span = last.bbox[hi] - first.bbox[lo];
  const sizes = sorted.reduce((sum, t) => sum + (t.bbox[hi] - t.bbox[lo]), 0);
  const gap = (span - sizes) / (sorted.length - 1);
  let cursor = first.bbox[hi] + gap;
  for (const t of sorted.slice(1, -1)) {
    const d = cursor - t.bbox[lo];
    deltas.set(t.id, axis === "h" ? [d, 0] : [0, d]);
    cursor += t.bbox[hi] - t.bbox[lo] + gap;
  }
  return deltas;
}

/** Align the selected annotations against the selection's own bounding
 *  box. Returns a NEW list (unchanged annotations keep identity). */
export function alignAnnots(
  annots: readonly Annotation[],
  ids: ReadonlySet<string>,
  mode: AlignMode
): Annotation[] {
  const targets = annots.filter((a) => ids.has(a.id) && movable(a));
  if (targets.length < 2) return [...annots];
  const deltas = alignBoxes(
    targets.map((a) => ({ id: a.id, bbox: annotBounds(a) })),
    mode
  );
  return annots.map((a) => {
    const d = deltas.get(a.id);
    return !d || (d[0] === 0 && d[1] === 0) ? a : translateAnnot(a, d[0], d[1]);
  });
}

/** Distribute the selected annotations with equal gaps between their
 *  boxes; the outermost two stay put. Needs ≥3 movable targets. */
export function distributeAnnots(
  annots: readonly Annotation[],
  ids: ReadonlySet<string>,
  axis: "h" | "v"
): Annotation[] {
  const targets = annots.filter((a) => ids.has(a.id) && movable(a));
  if (targets.length < 3) return [...annots];
  const deltas = distributeBoxes(
    targets.map((a) => ({ id: a.id, bbox: annotBounds(a) })),
    axis
  );
  return annots.map((a) => {
    const d = deltas.get(a.id);
    return !d || (d[0] === 0 && d[1] === 0) ? a : translateAnnot(a, d[0], d[1]);
  });
}

/** Axis-aligned box intersection (marquee hit test). */
export function boxesIntersect(a: Box, b: Box): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}
